import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { auth, storage } from '../firebase';

interface ImageInput {
  id: string;
  url: string;
  path: string;
}

export const getCurrentUserAuthorization = async (forceRefresh = false) => {
  const { currentUser } = auth;
  if (currentUser) {
    const token = await currentUser.getIdToken(forceRefresh);
    return `Bearer ${token}`;
  }
  return '';
};

export const uploadFiles = async (
  images: ImageInput[],
  {
    onSuccess,
    isPublicUpload = false,
  }: {
    onSuccess?: (image: ImageInput) => void;
    isPublicUpload?: boolean;
  } = {},
) => {
  const user = auth.currentUser;
  const authServiceId = user?.uid;
  if (!authServiceId && !isPublicUpload) throw new Error('Not authenticated');

  const transferPromises: Promise<string>[] = [];

  for (let i = 0; i < images.length; i += 1) {
    const image = images[i];

    const response = await fetch(image.url as URL | RequestInfo);
    // fetch() resolves on 4xx/5xx, so without this the error body would be
    // uploaded and getDownloadURL would hand back a valid-looking URL for a
    // signed waiver or deposit agreement that is really a JSON error blob.
    if (!response.ok) {
      throw new Error(
        `Failed to read file for upload (${response.status} ${response.statusText})`,
      );
    }
    const blob = await response.blob();

    transferPromises.push(
      new Promise((resolve, reject) => {
        const storageUrl = image.path;
        const permanentImageRef = ref(storage, storageUrl);
        const uploadTask = uploadBytesResumable(permanentImageRef, blob);
        uploadTask.on(
          'state_changed',
          () => {},
          uploadError => reject(uploadError),
          async () => {
            try {
              onSuccess?.(image);
              const downloadURL = await getDownloadURL(permanentImageRef);
              resolve(downloadURL);
            } catch (completionError) {
              reject(completionError);
            }
          },
        );
      }),
    );
  }

  const imgUrls = await Promise.all(transferPromises);
  return imgUrls;
};
